import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Power, Bike, Star, ChevronRight, MapPin, Wallet, Truck, RefreshCcw, Phone, Navigation, UserCog } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import RoleSwitcher from "../../components/RoleSwitcher";
import { playNotifSound } from "../../lib/notifSound";
import { raiseOrderAlert, riderGetsRideAlerts, userWantsPersistentAlerts } from "../../lib/orderAlert";
import { EVENT_NEW_RIDE, MODE_DISABLED, MODE_FULL_SCREEN, resolveRule } from "../../lib/notificationRules";
import { useTenant } from "../../contexts/TenantContext";
import { openMap } from "../../lib/openMap";
import { lseOn } from "../../lib/lse";

const STATUSES = [
    { key: "AVAILABLE", label: "Available", color: "bg-emerald-500" },
    { key: "ON_BREAK", label: "On break", color: "bg-amber-500" },
    { key: "OFFLINE", label: "Offline", color: "bg-zinc-400" },
];

export default function RiderHome() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [me, setMe] = useState(null);
    const [active, setActive] = useState([]);
    const [available, setAvailable] = useState([]);
    const [busy, setBusy] = useState(null);

    // Track ids of available rides we've already chimed for so a
    // re-fetch of the same row doesn't re-trigger the bell. The set
    // grows with each new ride and resets on unmount.
    const seenAvail = useRef(new Set());
    const firstLoad = useRef(true);
    const { tenant } = useTenant() || {};
    // How this tenant configured "Delivery request" (Admin -> Notifications). With no rule saved this resolves to
    // the legacy behaviour, so a tenant that never opens that page keeps exactly what it has today.
    const rideRule = useRef(null);
    rideRule.current = resolveRule(tenant, EVENT_NEW_RIDE);
    const alertsOn = useRef(false);
    alertsOn.current = rideRule.current.mode === MODE_FULL_SCREEN && userWantsPersistentAlerts(user);
    const tenantRef = useRef(tenant);
    tenantRef.current = tenant;
    const meRef = useRef(null);

    const reload = async () => {
        const [pMe, pMine, pAvail] = await Promise.allSettled([
            api.get("/rider/me"),
            api.get("/rider/orders/mine"),
            api.get("/rider/orders/available"),
        ]);
        if (pMe.status === "fulfilled") { setMe(pMe.value.data); meRef.current = pMe.value.data; }
        if (pMine.status === "fulfilled") setActive(pMine.value.data || []);
        if (pAvail.status === "fulfilled") {
            const list = pAvail.value.data || [];
            setAvailable(list);
            // Detect a brand-new ride: any id we haven't seen yet
            // (skipping the first load so we don't beep on cold start).
            if (!firstLoad.current) {
                const fresh = list.filter((o) => o?.id && !seenAvail.current.has(o.id));
                // Same persistent alert the ride push raises (keyed by order, so the two never double up) — and,
                // like the push, only for a rider the ride was actually offered to. Everyone else keeps the chime.
                const rule = rideRule.current;
                if (fresh.length && alertsOn.current && riderGetsRideAlerts(meRef.current, tenantRef.current)) {
                    fresh.forEach((o) => raiseOrderAlert({
                        key: `new_ride:${o.id}`, kind: "new_ride", orderId: o.id,
                        orderNumber: o.order_unique_id || o.id.slice(0, 8),
                        amount: o.totals?.grand_total ?? "", body: o.restaurant_name ? `Pickup: ${o.restaurant_name}` : "",
                        path: "/rider", repeat: rule.repeat, sound: rule.sound,
                    }));
                } else if (fresh.length && rule.mode !== MODE_DISABLED && rule.sound) {
                    // Switched off, or set to deliver silently: no chime either — the refresh must not say
                    // something the push wouldn't.
                    playNotifSound();
                }
            }
            list.forEach((o) => { if (o?.id) seenAvail.current.add(o.id); });
            firstLoad.current = false;
        }
    };
    useEffect(() => { reload(); const id = setInterval(reload, 8000); return () => clearInterval(id); }, []);

    const setStatus = async (status) => {
        try { await api.patch(`/rider/status?status=${status}`); toast.success(`Status → ${status}`); reload(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const accept = async (oid) => {
        setBusy(oid);
        try {
            await api.post(`/rider/orders/${oid}/accept`);
            toast.success("Order accepted");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally { setBusy(null); }
    };

    return (
        <div className="bizil-rider max-w-md mx-auto pb-20 safe-pb">
            {/* Header */}
            <div className="bg-gradient-to-br from-violet-600 to-indigo-700 text-white px-4 py-5 rounded-b-3xl safe-pt">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center"><Bike size={20} /></div>
                        <div>
                            <p className="bizil-type-label text-white/85">Rider</p>
                            <p className="font-bold text-lg leading-tight">{me?.name || user?.first_name || "Rider"}</p>
                            <p className="text-xs opacity-90 inline-flex items-center gap-1"><Star size={11} /> {me?.rating?.toFixed(1) || "5.0"} · {me?.total_deliveries || 0} deliveries</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <RoleSwitcher />
                        <Link
                            to="/rider/account"
                            data-testid="rider-account-link"
                            className="touch-target rounded-lg hover:bg-white/10 active:bg-white/20 inline-flex items-center justify-center"
                            aria-label="Account & settings"
                        ><UserCog size={18} /></Link>
                    </div>
                </div>

                {/* Status toggle — taller so it's reachable with gloves */}
                <div className="mt-4 bg-white/10 rounded-2xl p-1.5 flex">
                    {STATUSES.map((s) => (
                        <button key={s.key} data-testid={`rider-status-${s.key}`} onClick={() => setStatus(s.key)}
                            className={`flex-1 text-xs font-bold rounded-xl py-3 inline-flex items-center justify-center gap-1.5 transition ${me?.status === s.key ? "bg-white text-zinc-900 shadow" : "text-white/85"}`}>
                            <span className={`w-2 h-2 rounded-full ${s.color}`} />{s.label}
                        </button>
                    ))}
                </div>

                {/* Quick earnings tile — links to the full Earnings page */}
                <Link to="/rider/earnings" data-testid="rider-earnings-link" className="mt-3 flex items-center justify-between bg-white/15 rounded-2xl px-4 py-2.5 active:scale-[0.98] transition">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">My earnings</p>
                        <p className="text-xs opacity-90">Tap to see balance & ledger</p>
                    </div>
                    <Wallet size={18} />
                </Link>
            </div>

            <div className="px-4 mt-4 flex items-center justify-between">
                <h2 className="font-bold text-base">Active deliveries</h2>
                <button onClick={reload} className="text-xs font-bold text-primary inline-flex items-center gap-1"><RefreshCcw size={12} /> Refresh</button>
            </div>

            {active.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No active deliveries.</p>
            ) : (
                <div className="px-4 space-y-2 mt-2">
                    {active.map((o) => <OrderCard key={o.id} o={o} kind="active" />)}
                </div>
            )}

            <div className="px-4 mt-6 flex items-center justify-between">
                <h2 className="font-bold text-base">Available pickups · {available.length}</h2>
                <span className="inline-flex items-center gap-3">
                    {/* Location Service Engine village routes (only for tenants with them on). */}
                    {lseOn(tenant, "route_batching") && <Link to="/rider/route" data-testid="rider-route-link" className="text-xs font-bold text-primary inline-flex items-center gap-0.5">Today's route <ChevronRight size={12} /></Link>}
                    <Link to="/rider/history" data-testid="rider-history-link" className="text-xs font-bold text-primary inline-flex items-center gap-0.5">History <ChevronRight size={12} /></Link>
                </span>
            </div>

            {(me?.status !== "AVAILABLE") && (
                <p className="px-4 mt-2 text-xs text-amber-600">Set status to <strong>Available</strong> to accept new orders.</p>
            )}

            {available.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No orders waiting for pickup.</p>
            ) : (
                <div className="px-4 space-y-2 mt-2">
                    {available.map((o) => (
                        <OrderCard
                            key={o.id} o={o} kind="available"
                            onAccept={() => accept(o.id)} accepting={busy === o.id}
                            disabled={me?.status !== "AVAILABLE"}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function OrderCard({ o, kind, onAccept, accepting, disabled }) {
    const adr = o.delivery_address || {};
    const cdc = !!o.cdc_enabled;
    // iter134h — build the raw params here; openMap picks the correct
    // native URL scheme per platform (geo: on Android, maps:// on iOS,
    // https on web). Passing an https URL through openExternal caused
    // the Android WebView to attempt an intent:// handoff that failed
    // with net::ERR_UNKNOWN_URL_SCHEME.
    // iter134e3 — pass ``kind`` so openMap enforces lat/lng and
    // shows a specific "location not provided" toast if the source
    // order lacks coordinates instead of silently searching by name
    // and opening the wrong place.
    const pickup = {
        lat: o.restaurant_location?.lat,
        lng: o.restaurant_location?.lng,
        label: o.restaurant_name || "",
        kind: "pickup",
    };
    const drop = {
        lat: adr.lat,
        lng: adr.lng,
        label: [adr.line1, adr.city].filter(Boolean).join(", "),
        address: [adr.line1, adr.city].filter(Boolean).join(", "),
        kind: "drop",
    };

    return (
        <Link to={`/rider/orders/${o.id}`} data-testid={`rider-order-${o.id}`} className="block bg-card border border-border rounded-2xl p-3 active:scale-[0.99] transition">
            <div className="flex items-center gap-2 flex-wrap text-[10px]">
                <span className="font-bold text-foreground text-xs">{o.order_unique_id}</span>
                <Badge>{o.status}</Badge>
                {o.rider_status && <Badge tone="violet">{o.rider_status.replace(/_/g, " ")}</Badge>}
                {o.zone_name && <Badge tone="sky"><MapPin size={9} className="inline mr-0.5" />{o.zone_name}</Badge>}
                {cdc && <Badge tone="violet">CDC</Badge>}
                {cdc && o.cdc_paid_by_rider && <Badge tone="emerald">CDC paid</Badge>}
            </div>
            <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 items-start">
                <div>
                    <p className="text-xs"><strong>Pickup:</strong> {o.restaurant_name}</p>
                    <p className="text-xs mt-0.5"><strong>Drop:</strong> {[adr.line1, adr.city].filter(Boolean).join(", ")}</p>
                </div>
                <div className="text-right">
                    <p className="text-sm font-bold">{inr(o.totals?.grand_total)}</p>
                    {(o.rider_payout != null) && (
                        <p className="text-[11px] text-foreground font-bold inline-flex items-center gap-1" data-testid={`rider-payout-${o.id}`}>
                            <Bike size={10} /> Earn {inr(o.rider_payout)}
                        </p>
                    )}
                    {cdc ? (
                        <p className="text-[11px] text-violet-700 font-semibold inline-flex items-center gap-1"><Truck size={10} /> Pay {inr(o.totals?.merchant_payout || 0)}</p>
                    ) : o.payment_method === "COD" ? (
                        <p className="text-[11px] text-amber-700 font-semibold inline-flex items-center gap-1"><Wallet size={10} /> Collect</p>
                    ) : (
                        <p className="text-[11px] text-emerald-700 font-semibold">Prepaid</p>
                    )}
                </div>
            </div>
            <div className="mt-2.5 flex items-center gap-2">
                <button
                    type="button"
                    data-testid={`rider-nav-pickup-${o.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openMap(pickup); }}
                    className="flex-1 inline-flex items-center justify-center gap-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-lg py-2.5 text-xs font-bold min-h-[44px]"
                ><Navigation size={12} /> To pickup</button>
                <button
                    type="button"
                    data-testid={`rider-nav-drop-${o.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openMap(drop); }}
                    className="flex-1 inline-flex items-center justify-center gap-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-lg py-2.5 text-xs font-bold min-h-[44px]"
                ><Navigation size={12} /> To drop</button>
                {o.restaurant_phone && (
                    <button
                        type="button"
                        data-testid={`call-shop-${o.id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${o.restaurant_phone}`; }}
                        className="touch-target bg-amber-50 text-amber-700 border border-amber-200 rounded-lg"
                        aria-label="Call shop"
                        title={`Call ${o.restaurant_name || "shop"}: ${o.restaurant_phone}`}
                    ><Phone size={14} /></button>
                )}
                {o.customer_mobile && (
                    <button
                        type="button"
                        data-testid={`call-customer-${o.id}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${o.customer_mobile}`; }}
                        className="touch-target bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg"
                        aria-label="Call customer"
                        title={`Call customer: ${o.customer_mobile}`}
                    ><Phone size={14} /></button>
                )}
            </div>
            {kind === "available" && (
                <button
                    data-testid={`rider-accept-${o.id}`}
                    onClick={(e) => { e.preventDefault(); if (!disabled) onAccept(); }}
                    disabled={accepting || disabled}
                    className="bizil-rider-action mt-3 w-full bg-foreground text-background rounded-xl text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-1"
                >
                    {accepting
                        ? "Accepting…"
                        : disabled
                        ? "Set status to Available to accept"
                        : (
                            <span className="inline-flex items-baseline gap-1.5">
                                <span>Accept</span>
                                {(o.rider_payout != null) && (
                                    <span className="text-base font-black" data-testid={`rider-accept-payout-${o.id}`}>
                                        · Earn {inr(o.rider_payout)}
                                    </span>
                                )}
                            </span>
                        )}
                </button>
            )}
        </Link>
    );
}

function Badge({ children, tone }) {
    const tones = { sky: "bg-sky-100 text-sky-700", violet: "bg-violet-100 text-violet-700", emerald: "bg-emerald-100 text-emerald-700" };
    const cls = tone ? tones[tone] : "bg-amber-100 text-amber-700";
    return <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${cls}`}>{children}</span>;
}
